import { useMemo, useState } from 'react';
import { Pencil, Plus, Trash2, UserCog } from 'lucide-react';
import {
  BosDurum,
  Buton,
  OnayDialog,
  Rozet,
  VeriTablo,
  toast,
  type ColumnDef,
  type RozetTonu,
} from '@emanetx/ui';
import type { Kullanici, KullaniciDurumu } from '@emanetx/sdk';
import { useKullanicilar, useKullaniciSil } from './api.js';
import { KullaniciDialog } from './kullanici-dialog.js';

const DURUM_TONU: Record<KullaniciDurumu, RozetTonu> = {
  AKTIF: 'yesil',
  PASIF: 'gri',
  ENGELLI: 'kirmizi',
};

const DURUM_ETIKET: Record<KullaniciDurumu, string> = {
  AKTIF: 'Aktif',
  PASIF: 'Pasif',
  ENGELLI: 'Engelli',
};

export function SayfaKullanicilar() {
  const [sayfa, setSayfa] = useState(1);
  const [dialogAcik, setDialogAcik] = useState(false);
  const [duzenleHedef, setDuzenleHedef] = useState<Kullanici | null>(null);
  const [silHedef, setSilHedef] = useState<Kullanici | null>(null);

  const liste = useKullanicilar(sayfa, 50);
  const sil = useKullaniciSil();

  const kolonlar = useMemo<ColumnDef<Kullanici, unknown>[]>(
    () => [
      {
        accessorKey: 'adSoyad',
        header: 'Ad Soyad',
        meta: { etiket: 'Ad Soyad' },
        cell: ({ row }) => (
          <div>
            <div className="font-medium">{row.original.adSoyad}</div>
            <div className="text-fume-500 text-xs">{row.original.eposta}</div>
          </div>
        ),
      },
      {
        id: 'roller',
        header: 'Roller',
        meta: { etiket: 'Roller' },
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.roller.length === 0 ? (
              <span className="text-fume-400 text-xs">—</span>
            ) : (
              row.original.roller.map((r) => (
                <Rozet key={r} ton="fume">
                  {r}
                </Rozet>
              ))
            )}
          </div>
        ),
      },
      {
        accessorKey: 'telefon',
        header: 'Telefon',
        meta: { etiket: 'Telefon' },
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.telefon ?? '—'}</span>,
      },
      {
        accessorKey: 'durum',
        header: 'Durum',
        meta: { etiket: 'Durum' },
        cell: ({ row }) => (
          <Rozet ton={DURUM_TONU[row.original.durum]} nokta>
            {DURUM_ETIKET[row.original.durum]}
          </Rozet>
        ),
      },
      {
        accessorKey: 'olusturmaTarihi',
        header: 'Eklendi',
        meta: { etiket: 'Eklendi' },
        cell: ({ row }) => (
          <span className="text-fume-500 font-mono text-xs">
            {new Date(row.original.olusturmaTarihi).toLocaleDateString('tr-TR', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })}
          </span>
        ),
      },
      {
        id: 'aksiyon',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            <Buton
              stil="sade"
              boyut="simge"
              aria-label="Düzenle"
              onClick={() => {
                setDuzenleHedef(row.original);
                setDialogAcik(true);
              }}
            >
              <Pencil size={14} />
            </Buton>
            <Buton
              stil="sade"
              boyut="simge"
              aria-label="Sil"
              onClick={() => setSilHedef(row.original)}
            >
              <Trash2 size={14} className="text-red-500" />
            </Buton>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Kullanıcılar</h1>
          <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
            Admin paneli erişimi olan kullanıcılar ve rolleri.
          </p>
        </div>
        <Buton
          onClick={() => {
            setDuzenleHedef(null);
            setDialogAcik(true);
          }}
        >
          <Plus size={16} /> Yeni Kullanıcı
        </Buton>
      </header>

      <VeriTablo
        veri={liste.data?.veriler ?? []}
        kolonlar={kolonlar}
        toplam={liste.data?.toplam}
        sayfa={sayfa}
        boyut={50}
        onSayfa={setSayfa}
        yukleniyor={liste.isLoading}
        yenileniyor={liste.isFetching}
        onYenile={() => void liste.refetch()}
        bosDurum={
          <BosDurum
            ikon={UserCog}
            baslik="Henüz kullanıcı yok"
            aciklama="İlk admin kullanıcısını ekleyerek başlayın."
          />
        }
      />

      <KullaniciDialog
        acik={dialogAcik}
        onDegisim={(o) => {
          setDialogAcik(o);
          if (!o) setDuzenleHedef(null);
        }}
        kullanici={duzenleHedef}
      />

      <OnayDialog
        acik={!!silHedef}
        onDegisim={(o) => !o && setSilHedef(null)}
        baslik="Kullanıcıyı sil?"
        aciklama={silHedef ? `${silHedef.adSoyad} (${silHedef.eposta})` : ''}
        onayMetni="Sil"
        tur="tehlike"
        onOnay={async () => {
          if (!silHedef) return;
          try {
            await sil.mutateAsync(silHedef.id);
            toast.success('Kullanıcı silindi');
            setSilHedef(null);
          } catch (err) {
            const m =
              err && typeof err === 'object' && 'message' in err
                ? String((err as { message: string }).message)
                : 'Silinemedi';
            toast.error(m);
          }
        }}
      />
    </div>
  );
}
